import React, { useMemo } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import {
    CTableWrapper,
    DBlock,
    DBlockHeader,
    ICustomTableWrapperProps,
} from '../../../../dum';
import { formatFloat } from '../../../../../helpers';
import { MaterialBalanceExceptionsQuery } from '../../../../../services';

type Exceptions =
    MaterialBalanceExceptionsQuery['getMaterialBalanceExceptions'];
type RunException = Exceptions['runs_without_consumption'][number];
type ProductOrigin = Exceptions['products_packed'][number];

// Typed to the row type, not to `ICustomTableWrapperItem` (which is fixed to
// `Record<any, any>`): `content` is a function property, so under
// strictFunctionTypes its parameter is checked contravariantly, and
// `Record<any, any>` is NOT assignable to a specific record type. Annotating the
// parameters against the `Record` alias fails; naming the row type here both
// compiles and keeps the field names checked.
function useRunItems() {
    return useMemo<ICustomTableWrapperProps<RunException>['items']>(() => {
        return [
            {
                title: 'Fecha',
                textAlign: 'left',
                content: (item) => item.start_date,
            },
            {
                title: 'Turno',
                textAlign: 'left',
                content: (item) =>
                    item.shift === null || item.shift === undefined
                        ? '—'
                        : String(item.shift),
            },
            {
                title: 'Sucursal',
                textAlign: 'left',
                content: (item) => item.branch_name || '—',
            },
            {
                title: 'Máquinas',
                textAlign: 'left',
                cellGrow: 2,
                content: (item) => item.machine_names || '—',
            },
            {
                title: 'Bolsa producida',
                textAlign: 'right',
                content: (item) => formatFloat(item.kilos_produced, 0),
            },
            {
                title: 'Merma',
                textAlign: 'right',
                content: (item) => formatFloat(item.kilos_waste, 0),
            },
            {
                title: 'Bobina consumida',
                textAlign: 'right',
                content: (item) => formatFloat(item.kilos_consumed, 0),
            },
        ];
    }, []);
}

/**
 * The excepciones tables. Plain lists, no computation — each one exists to be
 * shown to a supervisor and asked about.
 */
export function MaterialBalanceExceptions({
    exceptions,
    loading,
}: {
    exceptions?: Exceptions;
    loading?: boolean;
}) {
    const runItems = useRunItems();

    const productItems = useMemo<
        ICustomTableWrapperProps<ProductOrigin>['items']
    >(() => {
        return [
            {
                title: 'Producto',
                textAlign: 'left',
                cellGrow: 3,
                content: (item) =>
                    item.product_description || `#${item.product_id}`,
            },
            {
                title: 'Kilos en empacado',
                textAlign: 'right',
                content: (item) => formatFloat(item.kilos_packed, 0),
            },
            {
                title: 'Kilos en máquina de corte',
                textAlign: 'right',
                content: (item) => formatFloat(item.kilos_cut, 0),
            },
            {
                title: 'Kilos vendidos',
                textAlign: 'right',
                content: (item) => formatFloat(item.kilos_sold, 0),
            },
        ];
    }, []);

    const runsWithoutConsumption = exceptions?.runs_without_consumption || [];
    const runsWithoutProduction = exceptions?.runs_without_production || [];

    // Memoised because `|| []` builds a fresh array on every render, which would
    // make it an unstable dependency of the filter below.
    const productsPacked = useMemo(() => {
        return exceptions?.products_packed || [];
    }, [exceptions]);

    const withoutCutOrigin = useMemo(() => {
        return productsPacked.filter((product) => product.kilos_cut === 0);
    }, [productsPacked]);

    return (
        <DBlock>
            <DBlockHeader subtitle={'Excepciones'} />

            <Typography variant={'subtitle2'} sx={{ mb: 1 }}>
                Corridas de corte sin consumo registrado (
                {runsWithoutConsumption.length})
            </Typography>
            <Alert severity={'info'} sx={{ mb: 2 }}>
                Excluye las áreas de empacado, que legítimamente no consumen
                bobina. Lo que aparece aquí es una captura faltante real, y la
                lista debería quedar corta.
            </Alert>
            <Box sx={{ mb: 4 }}>
                <CTableWrapper
                    items={runItems}
                    data={runsWithoutConsumption}
                    loading={loading}
                />
            </Box>

            <Typography variant={'subtitle2'} sx={{ mb: 1 }}>
                Corridas sin producción registrada (
                {runsWithoutProduction.length})
            </Typography>
            <Alert severity={'warning'} sx={{ mb: 2 }}>
                Se consumió bobina y no se registró nada de salida. Es la
                excepción inversa a la anterior y, con los datos de 2026, la más
                grande de las dos por dos órdenes de magnitud — buena parte de la
                diferencia positiva de la tabla de arriba vive aquí.
            </Alert>
            <Box sx={{ mb: 4 }}>
                <CTableWrapper
                    items={runItems}
                    data={runsWithoutProduction}
                    loading={loading}
                />
            </Box>

            <Typography variant={'subtitle2'} sx={{ mb: 1 }}>
                Productos producidos en empacado ({productsPacked.length}, de los
                cuales {withoutCutOrigin.length} sin origen en corte)
            </Typography>
            <Alert severity={'info'} sx={{ mb: 2 }}>
                Los que tienen <strong>0 kilos en máquina de corte</strong> son
                bolsa cuya materia prima nunca se resta del inventario: se
                cortaron en algún lado y bajo algún producto que no se registra.
                Los que sí tienen kilos en corte pueden estar contados dos veces —
                cuando los kilos vendidos superan la producción total, el registro
                es aditivo y no duplicado. Esta tabla existe para preguntarlo en
                piso, no para responderlo.
            </Alert>
            <Box>
                <CTableWrapper
                    items={productItems}
                    data={productsPacked}
                    loading={loading}
                    rowSx={(item: ProductOrigin) =>
                        item.kilos_cut === 0 ? { fontWeight: 'bold' } : {}
                    }
                />
            </Box>
        </DBlock>
    );
}
